import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import LinkIcon from '@mui/icons-material/Link';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { hostApiClient } from '../../hostApiClient';
import { Room } from '../../models/Room';
import {
  GenerateSmartLockPasscodeResponse,
  LinkedRoomsByLockResponse,
  SmartLockSettingsResponse,
  SmartLockSummary,
} from '../../models/SmartLock';
import { NotificationSnackbar } from '../../components/common/NotificationSnackbar';

export default function SmartLocksPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<SmartLockSettingsResponse | null>(null);
  const [locks, setLocks] = useState<SmartLockSummary[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [linkedByLock, setLinkedByLock] = useState<LinkedRoomsByLockResponse[]>([]);

  const [settingsUsername, setSettingsUsername] = useState('');
  const [settingsBreathingRoom, setSettingsBreathingRoom] = useState(60);
  const [settingsPassword, setSettingsPassword] = useState('');
  const [settingsPasswordTouched, setSettingsPasswordTouched] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [deleteSettingsDialogOpen, setDeleteSettingsDialogOpen] = useState(false);
  const [deletingSettings, setDeletingSettings] = useState(false);

  const [linkingRoom, setLinkingRoom] = useState(false);
  const [unlinkingRoomId, setUnlinkingRoomId] = useState<string | null>(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkDialogRoomId, setLinkDialogRoomId] = useState<string | null>(null);
  const [linkDialogLockId, setLinkDialogLockId] = useState<number | ''>('');

  const [passcodeDialogOpen, setPasscodeDialogOpen] = useState(false);
  const [passcodeDialogRoomId, setPasscodeDialogRoomId] = useState<string | null>(null);
  const [passcodeStartTime, setPasscodeStartTime] = useState<Date | null>(null);
  const [passcodeEndTime, setPasscodeEndTime] = useState<Date | null>(null);
  const [creatingPasscode, setCreatingPasscode] = useState(false);
  const [passcodeResult, setPasscodeResult] = useState<GenerateSmartLockPasscodeResponse | null>(null);

  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const linkedRoomIds = useMemo(() => {
    const ids = new Set<string>();
    linkedByLock.forEach((lockGroup) => {
      lockGroup.rooms.forEach((room) => ids.add(room.roomId));
    });
    return ids;
  }, [linkedByLock]);

  const linkedRoomLockNameByRoomId = useMemo(() => {
    const map = new Map<string, string>();
    linkedByLock.forEach((lockGroup) => {
      lockGroup.rooms.forEach((room) => {
        map.set(room.roomId, lockGroup.externalLockName);
      });
    });
    return map;
  }, [linkedByLock]);

  const lockLookup = useMemo(() => {
    const map = new Map<number, SmartLockSummary>();
    locks.forEach((l) => map.set(l.lockId, l));
    return map;
  }, [locks]);

  const isSettingsCompleted = Boolean(settings?.provider && settings?.username);
  const hasSettingsChanges = useMemo(() => {
    if (!settings) {
      return Boolean(settingsUsername.trim() || settingsPassword.trim() || settingsBreathingRoom !== 60);
    }

    const usernameChanged = settingsUsername.trim() !== (settings.username ?? '');
    const breathingRoomChanged =
      settingsBreathingRoom !== (settings.passcodeValidityBreathingRoomInMinutes ?? 0);
    const passwordProvided = Boolean(settingsPassword.trim());
    return usernameChanged || breathingRoomChanged || passwordProvided;
  }, [settings, settingsUsername, settingsPassword, settingsBreathingRoom]);

  const loadPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settingsResp, locksResp, roomsResp, linkedResp] = await Promise.all([
        hostApiClient.getSmartLockSettings().catch(() => null),
        hostApiClient.getSmartLocks().catch(() => []),
        hostApiClient.getRooms(),
        hostApiClient.getLinkedSmartLockRooms().catch(() => []),
      ]);
      setSettings(settingsResp);
      setLocks(locksResp);
      setRooms(roomsResp);
      setLinkedByLock(linkedResp);

      if (settingsResp) {
        setSettingsUsername(settingsResp.username ?? '');
        setSettingsBreathingRoom(settingsResp.passcodeValidityBreathingRoomInMinutes ?? 0);
        setSettingsPassword('');
        setSettingsPasswordTouched(false);
      }
    } catch {
      setError(t('smartLocks.loadError', 'Could not load Smart Locks data.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSaveSettings = async () => {
    if (settingsBreathingRoom < 0) {
      showSnackbar(t('smartLocks.breathingRoomInvalid', 'Breathing room must be 0 or greater.'), 'error');
      return;
    }

    if (!settingsUsername.trim()) {
      showSnackbar(t('smartLocks.usernameRequired', 'Username is required.'), 'error');
      return;
    }

    if (!settingsPassword.trim() && !settings) {
      showSnackbar(t('smartLocks.passwordRequiredFirstSetup', 'Provide password for first-time setup.'), 'error');
      return;
    }

    setSavingSettings(true);
    try {
      await hostApiClient.upsertSmartLockSettings({
        provider: 'TTLOCK',
        username: settingsUsername.trim(),
        passcodeValidityBreathingRoomInMinutes: settingsBreathingRoom,
        ...(settingsPassword.trim() ? { password: settingsPassword.trim() } : {}),
      });

      const refreshed = await hostApiClient.getSmartLockSettings();
      setSettings(refreshed);
      setSettingsPassword('');
      setSettingsPasswordTouched(false);
      showSnackbar(t('smartLocks.settingsSaved', 'Smart lock settings saved.'));
    } catch {
      showSnackbar(t('smartLocks.settingsSaveError', 'Could not save smart lock settings.'), 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDeleteSettings = async () => {
    setDeletingSettings(true);
    try {
      await hostApiClient.deleteSmartLockSettings();
      setSettings(null);
      setSettingsUsername('');
      setSettingsPassword('');
      setSettingsPasswordTouched(false);
      setSettingsBreathingRoom(60);
      setLinkedByLock([]);
      setPasscodeResult(null);
      setDeleteSettingsDialogOpen(false);
      showSnackbar(t('smartLocks.settingsDeleted', 'Smart lock settings deleted.'));
    } catch {
      showSnackbar(t('smartLocks.settingsDeleteError', 'Could not delete smart lock settings.'), 'error');
    } finally {
      setDeletingSettings(false);
    }
  };

  const handleOpenLinkDialog = (roomId: string) => {
    setLinkDialogRoomId(roomId);
    setLinkDialogLockId('');
    setLinkDialogOpen(true);
  };

  const handleCloseLinkDialog = () => {
    if (linkingRoom) return;
    setLinkDialogOpen(false);
    setLinkDialogRoomId(null);
    setLinkDialogLockId('');
  };

  const handleLinkRoom = async () => {
    if (!linkDialogRoomId || linkDialogLockId === '') {
      showSnackbar(t('smartLocks.selectLock', 'Select a lock.'), 'error');
      return;
    }

    const selectedLock = lockLookup.get(Number(linkDialogLockId));
    if (!selectedLock) {
      showSnackbar(t('smartLocks.lockNotFound', 'Selected lock not found.'), 'error');
      return;
    }

    setLinkingRoom(true);
    try {
      await hostApiClient.linkRoomToSmartLock(linkDialogRoomId, {
        lockId: selectedLock.lockId,
        lockName: selectedLock.lockName,
      });
      const linkedResp = await hostApiClient.getLinkedSmartLockRooms();
      setLinkedByLock(linkedResp);
      handleCloseLinkDialog();
      showSnackbar(t('smartLocks.roomLinked', 'Room linked to lock.'));
    } catch {
      showSnackbar(t('smartLocks.roomLinkError', 'Could not link room to lock.'), 'error');
    } finally {
      setLinkingRoom(false);
    }
  };

  const handleUnlinkRoom = async (roomId: string) => {
    setUnlinkingRoomId(roomId);
    try {
      await hostApiClient.unlinkRoomFromSmartLock(roomId);
      const linkedResp = await hostApiClient.getLinkedSmartLockRooms();
      setLinkedByLock(linkedResp);
      showSnackbar(t('smartLocks.roomUnlinked', 'Room unlinked from lock.'));
    } catch {
      showSnackbar(t('smartLocks.roomUnlinkError', 'Could not unlink room.'), 'error');
    } finally {
      setUnlinkingRoomId(null);
    }
  };

  const handleOpenPasscodeDialog = (roomId: string) => {
    const now = new Date();
    const defaultStart = getNextFullHour(now);
    const defaultEnd = new Date(now.getTime() + 2 * 60 * 60 * 1000);
    defaultEnd.setTime(defaultStart.getTime() + 2 * 60 * 60 * 1000);
    setPasscodeDialogRoomId(roomId);
    setPasscodeStartTime(defaultStart);
    setPasscodeEndTime(defaultEnd);
    setPasscodeResult(null);
    setPasscodeDialogOpen(true);
  };

  const handleClosePasscodeDialog = () => {
    if (creatingPasscode) return;
    setPasscodeDialogOpen(false);
    setPasscodeDialogRoomId(null);
    setPasscodeStartTime(null);
    setPasscodeEndTime(null);
  };

  const handleGeneratePasscode = async () => {
    if (!passcodeDialogRoomId || !passcodeStartTime || !passcodeEndTime) {
      showSnackbar(t('smartLocks.passcodeFieldsRequired', 'Room, start time, and end time are required.'), 'error');
      return;
    }

    if (passcodeStartTime >= passcodeEndTime) {
      showSnackbar(t('smartLocks.endAfterStart', 'End time must be after start time.'), 'error');
      return;
    }

    setCreatingPasscode(true);
    try {
      const selectedRoom = rooms.find((room) => room.id === passcodeDialogRoomId);
      const roomTimezone = selectedRoom?.spaceTimezone;
      if (!roomTimezone) {
        showSnackbar(t('smartLocks.timezoneMissing', 'Room timezone is missing. Please refresh and try again.'), 'error');
        setCreatingPasscode(false);
        return;
      }
      const startUtc = zonedTimeToUtc(passcodeStartTime, roomTimezone);
      const endUtc = zonedTimeToUtc(passcodeEndTime, roomTimezone);

      const result = await hostApiClient.generateSmartLockPasscode({
        roomId: passcodeDialogRoomId,
        startTime: formatInTimeZone(startUtc, 'UTC', "yyyy-MM-dd'T'HH:mm:ss"),
        endTime: formatInTimeZone(endUtc, 'UTC', "yyyy-MM-dd'T'HH:mm:ss"),
      });
      setPasscodeResult(result);
      showSnackbar(t('smartLocks.passcodeGenerated', 'Passcode generated.'));
    } catch {
      showSnackbar(t('smartLocks.passcodeGenerateError', 'Could not generate passcode.'), 'error');
    } finally {
      setCreatingPasscode(false);
    }
  };

  const handleCopyPasscode = async () => {
    if (!passcodeResult?.passcode) return;
    try {
      await navigator.clipboard.writeText(passcodeResult.passcode);
      showSnackbar(t('smartLocks.passcodeCopied', 'Passcode copied.'));
    } catch {
      showSnackbar(t('smartLocks.passcodeCopyError', 'Could not copy passcode.'), 'error');
    }
  };

  const passwordFieldValue =
    settings && !settingsPasswordTouched && !settingsPassword ? '********' : settingsPassword;

  const toFullHour = (value: Date | null): Date | null => {
    if (!value) return null;
    const normalized = new Date(value);
    const hasSubHourPart =
      normalized.getMinutes() > 0 || normalized.getSeconds() > 0 || normalized.getMilliseconds() > 0;
    normalized.setMinutes(0, 0, 0);
    if (hasSubHourPart) {
      normalized.setHours(normalized.getHours() + 1);
    }
    return normalized;
  };

  const getNextFullHour = (base: Date = new Date()): Date => {
    const next = new Date(base);
    next.setMinutes(0, 0, 0);
    if (next <= base) {
      next.setHours(next.getHours() + 1);
    }
    return next;
  };

  const nextAllowedHour = getNextFullHour();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          // Avoid `p` + `pb` together — the `p` shorthand can override bottom padding.
          px: { xs: 2, sm: 3, md: 4 },
          pt: { xs: 2, sm: 3, md: 4 },
          // Fixed site footer (~56px) sits over main; need enough scroll padding to clear it.
          pb: { xs: 14, md: 16 },
          maxWidth: isSettingsCompleted ? 1380 : 900,
          mx: 'auto',
          minHeight: 'calc(100vh - 140px)',
        }}
      >
      <Typography variant="h4" fontWeight={800} mb={1}>
        {t('smartLocks.title', 'Smart Locks')}
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={3.5}>
        {t('smartLocks.subtitle', 'Configure TTLock credentials, link rooms to locks, and generate guest passcodes.')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={{ xs: 2, md: 2.5 }}>
          <Grid
            item
            xs={12}
            md={isSettingsCompleted ? 6 : 10}
            lg={isSettingsCompleted ? 6 : 8}
            sx={{ mx: isSettingsCompleted ? 0 : 'auto' }}
          >
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                boxShadow: (theme) =>
                  theme.palette.mode === 'dark' ? '0 10px 30px rgba(0,0,0,0.35)' : '0 8px 20px rgba(0,0,0,0.08)',
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SettingsIcon fontSize="small" color="action" />
                    <Typography variant="h6">{t('smartLocks.settings', 'Settings')}</Typography>
                  </Stack>
                  {settings && (
                    <IconButton
                      onClick={() => setDeleteSettingsDialogOpen(true)}
                      disabled={savingSettings || deletingSettings}
                      size="small"
                      aria-label={t('smartLocks.deleteSettingsAria', 'Delete settings')}
                      sx={{
                        color: 'common.white',
                        '&:hover': {
                          color: 'common.white',
                        },
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </Stack>
                <Stack spacing={2}>
                  <TextField value="TTLOCK" label={t('smartLocks.provider', 'Provider')} disabled fullWidth />
                  <TextField
                    label={t('smartLocks.username', 'Username')}
                    value={settingsUsername}
                    onChange={(e) => setSettingsUsername(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label={t('smartLocks.password', 'Password')}
                    type="password"
                    value={passwordFieldValue}
                    onFocus={() => {
                      if (settings && !settingsPasswordTouched) {
                        setSettingsPasswordTouched(true);
                        setSettingsPassword('');
                      }
                    }}
                    onChange={(e) => {
                      setSettingsPasswordTouched(true);
                      setSettingsPassword(e.target.value);
                    }}
                    autoComplete="new-password"
                    fullWidth
                  />
                  <TextField
                    label={t('smartLocks.breathingRoom', 'Validity Breathing Room (minutes)')}
                    type="number"
                    inputProps={{ min: 0 }}
                    value={settingsBreathingRoom}
                    onChange={(e) => setSettingsBreathingRoom(Number(e.target.value))}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSaveSettings}
                    disabled={savingSettings || !hasSettingsChanges}
                    sx={{ mt: 0.5, py: 1.25, fontWeight: 700 }}
                  >
                    {savingSettings ? <CircularProgress size={20} /> : t('smartLocks.saveSettings', 'Save Settings')}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {isSettingsCompleted && (
            <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LockOpenIcon fontSize="small" color="action" />
                    <Typography variant="h6">{t('smartLocks.locks', 'Locks')}</Typography>
                  </Stack>
                  <Button size="small" startIcon={<RefreshIcon />} onClick={loadPage}>
                    {t('smartLocks.refresh', 'Refresh')}
                  </Button>
                </Stack>
                <Stack spacing={1}>
                  {locks.length === 0 ? (
                    <Typography color="text.secondary">{t('smartLocks.noLocks', 'No locks found.')}</Typography>
                  ) : (
                    locks.map((lock) => (
                      <Box
                        key={lock.lockId}
                        sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
                      >
                        <Typography fontWeight={600}>{lock.lockName}</Typography>
                        <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" useFlexGap>
                          <Chip size="small" label={t('smartLocks.lockId', 'ID: {{id}}', { id: lock.lockId })} />
                          <Chip size="small" label={t('smartLocks.battery', 'Battery: {{percent}}%', { percent: lock.electricQuantity })} />
                          <Chip size="small" label={lock.hasGateway ? t('smartLocks.gateway', 'Gateway') : t('smartLocks.noGateway', 'No Gateway')} />
                          {lock.groupName && <Chip size="small" label={lock.groupName} />}
                        </Stack>
                      </Box>
                    ))
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          )}

          {isSettingsCompleted && (
            <Grid item xs={12}>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Typography variant="h6" mb={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MeetingRoomIcon fontSize="small" color="action" />
                    <span>{t('smartLocks.roomLinking', 'Room to Lock Linking')}</span>
                  </Stack>
                </Typography>

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('smartLocks.colRoom', 'Room')}</TableCell>
                      <TableCell>{t('smartLocks.colLockName', 'Lock Name')}</TableCell>
                      <TableCell>{t('smartLocks.colLinked', 'Linked')}</TableCell>
                      <TableCell align="right">{t('smartLocks.colAction', 'Action')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rooms.map((room) => (
                      <TableRow key={room.id}>
                        <TableCell>{room.name}</TableCell>
                        <TableCell>{linkedRoomLockNameByRoomId.get(room.id) ?? '—'}</TableCell>
                        <TableCell>
                          {linkedRoomIds.has(room.id) ? (
                            <Chip size="small" color="success" label={t('smartLocks.linked', 'Linked')} />
                          ) : (
                            <Chip size="small" label={t('smartLocks.notLinked', 'Not linked')} />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            {!linkedRoomIds.has(room.id) && (
                              <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={() => handleOpenLinkDialog(room.id)}>
                                {t('smartLocks.link', 'Link')}
                              </Button>
                            )}
                            {linkedRoomIds.has(room.id) && (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<VpnKeyIcon />}
                                onClick={() => handleOpenPasscodeDialog(room.id)}
                              >
                                {t('smartLocks.generatePasscode', 'Generate Passcode')}
                              </Button>
                            )}
                            {linkedRoomIds.has(room.id) && (
                              <Button
                                size="small"
                                color="error"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => handleUnlinkRoom(room.id)}
                                disabled={unlinkingRoomId === room.id}
                              >
                                {unlinkingRoomId === room.id ? <CircularProgress size={16} /> : t('smartLocks.unlink', 'Unlink')}
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
          )}

        </Grid>
      )}

      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />

      <Dialog open={linkDialogOpen} onClose={handleCloseLinkDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <LinkIcon fontSize="small" />
            <span>{t('smartLocks.linkDialogTitle', 'Link Room to Lock')}</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <FormControl fullWidth>
              <InputLabel id="link-dialog-lock-select-label">{t('smartLocks.lock', 'Lock')}</InputLabel>
              <Select
                labelId="link-dialog-lock-select-label"
                label={t('smartLocks.lock', 'Lock')}
                value={linkDialogLockId}
                onChange={(e) => setLinkDialogLockId(Number(e.target.value))}
              >
                {locks.map((lock) => (
                  <MenuItem key={lock.lockId} value={lock.lockId}>
                    {lock.lockName} ({lock.lockId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLinkDialog} disabled={linkingRoom}>
            {t('smartLocks.cancel', 'Cancel')}
          </Button>
          <Button variant="contained" startIcon={<LinkIcon />} onClick={handleLinkRoom} disabled={linkingRoom || linkDialogLockId === ''}>
            {linkingRoom ? <CircularProgress size={20} /> : t('smartLocks.link', 'Link')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={passcodeDialogOpen} onClose={handleClosePasscodeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <VpnKeyIcon fontSize="small" />
            <span>{t('smartLocks.passcodeDialogTitle', 'Generate Passcode')}</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <DateTimePicker
              label={t('smartLocks.validFrom', 'Valid From')}
              ampm={false}
              value={passcodeStartTime}
              minDateTime={nextAllowedHour}
              timeSteps={{ hours: 1, minutes: 60, seconds: 60 }}
              onChange={(value) => {
                const normalized = toFullHour(value);
                if (!normalized) {
                  setPasscodeStartTime(null);
                  return;
                }
                setPasscodeStartTime(normalized < nextAllowedHour ? nextAllowedHour : normalized);
              }}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DateTimePicker
              label={t('smartLocks.validTo', 'Valid To')}
              ampm={false}
              value={passcodeEndTime}
              minDateTime={
                passcodeStartTime
                  ? new Date(passcodeStartTime.getTime() + 60 * 60 * 1000)
                  : new Date(nextAllowedHour.getTime() + 60 * 60 * 1000)
              }
              timeSteps={{ hours: 1, minutes: 60, seconds: 60 }}
              onChange={(value) => {
                const normalized = toFullHour(value);
                if (!normalized) {
                  setPasscodeEndTime(null);
                  return;
                }
                const minimumEnd = passcodeStartTime
                  ? new Date(passcodeStartTime.getTime() + 60 * 60 * 1000)
                  : new Date(nextAllowedHour.getTime() + 60 * 60 * 1000);
                setPasscodeEndTime(normalized < minimumEnd ? minimumEnd : normalized);
              }}
              slotProps={{ textField: { fullWidth: true } }}
            />

            {passcodeResult && (
              <Box mt={1} p={2} border={1} borderColor="divider" borderRadius={1}>
                <Typography variant="subtitle2" color="text.secondary">
                  {t('smartLocks.generatedPasscode', 'Generated Passcode')}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
                  <Typography
                    variant="h3"
                    fontWeight={800}
                    sx={{ letterSpacing: '0.12em', lineHeight: 1.15 }}
                  >
                    {passcodeResult.passcode}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleCopyPasscode}
                    aria-label={t('smartLocks.copyPasscodeAria', 'Copy passcode')}
                    sx={{ alignSelf: 'center' }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasscodeDialog} disabled={creatingPasscode}>
            {t('smartLocks.close', 'Close')}
          </Button>
          <Button variant="contained" startIcon={<VpnKeyIcon />} onClick={handleGeneratePasscode} disabled={creatingPasscode}>
            {creatingPasscode ? <CircularProgress size={20} /> : t('smartLocks.generate', 'Generate')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteSettingsDialogOpen} onClose={() => !deletingSettings && setDeleteSettingsDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('smartLocks.deleteSettingsTitle', 'Delete Smart Lock Settings')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('smartLocks.deleteSettingsBody', 'This will remove your smart lock credentials and unlink access from this configuration screen.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSettingsDialogOpen(false)} disabled={deletingSettings}>
            {t('smartLocks.cancel', 'Cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteSettings} disabled={deletingSettings}>
            {deletingSettings ? <CircularProgress size={20} /> : t('smartLocks.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </LocalizationProvider>
  );
}
